'use client';

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/cn';

import styles from './AnimatedBlob.module.scss';

export const AnimatedBlob: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const interactiveRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(true);
  const [hasMouse, setHasMouse] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const container = containerRef.current;
    if (!container) {
      console.warn('AnimatedBlob: containerRef is not set');
      return;
    }

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = () => setHasMouse(true);
    window.addEventListener('mousemove', handleMouseMove, { once: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    if (!hasMouse || !interactiveRef.current || !containerRef.current) {
      return;
    }

    let curX = 0;
    let curY = 0;
    let tgX = 0;
    let tgY = 0;

    let rafId: number;

    const move = () => {
      curX += (tgX - curX) / 20;
      curY += (tgY - curY) / 20;

      if (interactiveRef.current) {
        interactiveRef.current.style.transform = `translate(${Math.round(curX)}px, ${Math.round(curY)}px)`;
      }

      rafId = requestAnimationFrame(move);
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      tgX = event.clientX - rect.left;
      tgY = event.clientY - rect.top;
    };

    // Attach to window instead of container
    window.addEventListener('mousemove', handleMouseMove);
    move();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [hasMouse]);

  return (
    <div
      ref={containerRef}
      className={cn(styles.animatedBlobWrapper, { [styles.paused]: !isInView })}
    >
      <div className={styles.animatedBlob}>
        <svg xmlns='http://www.w3.org/2000/svg' className={styles.noiseBg}>
          <filter id='noiseFilterBg'>
            <feTurbulence
              type='fractalNoise'
              baseFrequency='0.6'
              stitchTiles='stitch'
            />
          </filter>

          <rect
            width='100%'
            height='100%'
            preserveAspectRatio='xMidYMid meet'
            filter='url(#noiseFilterBg)'
          />
        </svg>
        <svg xmlns='http://www.w3.org/2000/svg' className={styles.svgBlur}>
          <defs>
            <filter id='goo'>
              <feGaussianBlur
                in='SourceGraphic'
                stdDeviation='10'
                result='blur'
              />
              <feColorMatrix
                in='blur'
                mode='matrix'
                values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8'
                result='goo'
              />
              <feBlend in='SourceGraphic' in2='goo' />
            </filter>
          </defs>
        </svg>
        <div className={styles.interactiveContainer}>
          <div className={styles.gradientsContainer}>
            <div className={styles.g1}></div>
            <div className={styles.g2}></div>
            <div className={styles.g3}></div>
            <div className={styles.g4}></div>
            <div className={styles.g5}></div>
            {hasMouse && (
              <div ref={interactiveRef} className={styles.interactive}></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
